import React, { useState, useEffect } from 'react';
import { FaHandshake, FaSearch, FaPlus, FaEdit, FaTrash, FaPhone, FaEnvelope } from 'react-icons/fa';
import axios from 'axios';

const Samarbejdspartnere = () => {
  const [partnere, setPartnere] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    navn: '',
    type: '',
    kontaktperson: '',
    telefon: '',
    email: '',
    adresse: '',
    beskrivelse: '',
    status: 'Aktiv'
  });

  useEffect(() => {
    fetchPartnere();
  }, []);

  const fetchPartnere = async () => {
    try {
      const response = await axios.get('/api/vaerksted/samarbejdspartnere');
      setPartnere(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching partnere:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPartner) {
        await axios.put(`/api/vaerksted/samarbejdspartnere/${editingPartner.id}`, formData);
      } else {
        await axios.post('/api/vaerksted/samarbejdspartnere', formData);
      }
      setShowModal(false);
      setEditingPartner(null);
      setFormData({
        navn: '',
        type: '',
        kontaktperson: '',
        telefon: '',
        email: '',
        adresse: '',
        beskrivelse: '',
        status: 'Aktiv'
      });
      fetchPartnere();
    } catch (error) {
      console.error('Error saving partner:', error);
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      navn: partner.navn || '',
      type: partner.type || '',
      kontaktperson: partner.kontaktperson || '',
      telefon: partner.telefon || '',
      email: partner.email || '',
      adresse: partner.adresse || '',
      beskrivelse: partner.beskrivelse || '',
      status: partner.status || 'Aktiv'
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Er du sikker på, at du vil slette denne samarbejdspartner?')) {
      try {
        await axios.delete(`/api/vaerksted/samarbejdspartnere/${id}`);
        fetchPartnere();
      } catch (error) {
        console.error('Error deleting partner:', error);
      }
    }
  };

  const filteredPartnere = partnere.filter(partner =>
    partner.navn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    partner.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    partner.kontaktperson?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Samarbejdspartnere</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-falck-blue text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
        >
          <FaPlus /> Tilføj partner
        </button>
      </div>

      {/* Søgefelt */}
      <div className="mb-6">
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Søg efter partnere..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-falck-blue focus:border-transparent"
          />
        </div>
      </div>

      {/* Partnere grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPartnere.map((partner) => (
          <div key={partner.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{partner.navn}</h3>
                <p className="text-sm text-gray-500">{partner.type}</p>
              </div>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                partner.status === 'Aktiv' ? 'bg-green-100 text-green-800' :
                partner.status === 'Inaktiv' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {partner.status}
              </span>
            </div>
            
            {partner.kontaktperson && (
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-medium">Kontaktperson:</span> {partner.kontaktperson}
              </p>
            )}
            
            {partner.telefon && (
              <p className="text-sm text-gray-600 mb-2 flex items-center gap-2">
                <FaPhone className="text-gray-400" />
                {partner.telefon}
              </p>
            )}
            
            {partner.email && (
              <p className="text-sm text-gray-600 mb-2 flex items-center gap-2">
                <FaEnvelope className="text-gray-400" />
                <a href={`mailto:${partner.email}`} className="text-falck-blue hover:underline">
                  {partner.email}
                </a>
              </p>
            )}
            
            {partner.adresse && (
              <p className="text-sm text-gray-600 mb-4">
                {partner.adresse}
              </p>
            )}
            
            {partner.beskrivelse && (
              <p className="text-sm text-gray-600 mb-4">
                {partner.beskrivelse}
              </p>
            )}
            
            <div className="flex justify-end gap-2">
              <button
                onClick={() => handleEdit(partner)}
                className="text-falck-blue hover:text-blue-700 p-1"
              >
                <FaEdit />
              </button>
              <button
                onClick={() => handleDelete(partner.id)}
                className="text-red-600 hover:text-red-900 p-1"
              >
                <FaTrash />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingPartner ? 'Rediger samarbejdspartner' : 'Tilføj ny samarbejdspartner'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Navn
                </label>
                <input
                  type="text"
                  value={formData.navn}
                  onChange={(e) => setFormData({...formData, navn: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  required
                >
                  <option value="">Vælg type</option>
                  <option value="Værksted">Værksted</option>
                  <option value="Leverandør">Leverandør</option>
                  <option value="Servicepartner">Servicepartner</option>
                  <option value="Andet">Andet</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kontaktperson
                </label>
                <input
                  type="text"
                  value={formData.kontaktperson}
                  onChange={(e) => setFormData({...formData, kontaktperson: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefon
                </label>
                <input
                  type="tel"
                  value={formData.telefon}
                  onChange={(e) => setFormData({...formData, telefon: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse
                </label>
                <textarea
                  value={formData.adresse}
                  onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  rows="2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beskrivelse
                </label>
                <textarea
                  value={formData.beskrivelse}
                  onChange={(e) => setFormData({...formData, beskrivelse: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  rows="3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                >
                  <option value="Aktiv">Aktiv</option>
                  <option value="Inaktiv">Inaktiv</option>
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingPartner(null);
                    setFormData({
                      navn: '',
                      type: '',
                      kontaktperson: '',
                      telefon: '',
                      email: '',
                      adresse: '',
                      beskrivelse: '',
                      status: 'Aktiv'
                    });
                  }}
                  className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Annuller
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-falck-blue text-white rounded-md hover:bg-blue-700"
                >
                  {editingPartner ? 'Opdater' : 'Tilføj'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Samarbejdspartnere;
